// import Honeybadger from 'honeybadger-js';
import { z } from 'zod';
import { filter, pairwise, take } from 'rxjs/operators';
import i18next from 'i18next';
import { zodI18nMap } from "zod-i18n-map";
import zod_en from 'zod-i18n-map/locales/en/zod.json';
import zod_de from '../assets/locale/zod-de.json';
import { ChangeDetectionStrategy, Component, HostListener, Inject, signal } from "@angular/core";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivationEnd, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { PrimeNGConfig } from "primeng/api";
import { Title } from "@angular/platform-browser";
import * as FlatFacades from '@medsurf/flat-facades';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatServices from '@medsurf/flat-services';
import { FLAT_SETTINGS, type SettingControlModel } from '@medsurf/flat-services';
import { from, interval } from 'rxjs';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SessionRefreshDialogComponent } from './common/session-refresh-dialog/session-refresh-dialog.component';

@Component({
    selector: 'medsurf-app',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent {
  #lastSessionCheck: number = Date.now();
  #refreshDialogRef?: DynamicDialogRef;
  #title?: string;
  readonly FlatModels = FlatModels;

  constructor(public appSharedFacade: FlatFacades.AppSharedFacade,
              public alertControlFacade: FlatFacades.AlertControlFacade,
              public historySharedFacade: FlatFacades.HistorySharedFacade,
              public router: Router,
              public authControlFacade: FlatFacades.AuthControlFacade,
              public navigationControlFacade: FlatFacades.NavigationControlFacade,
              public themeControlFacade: FlatFacades.ThemeControlFacade,
              public authContorlService: FlatServices.AuthControlService,
              public translate: TranslateService,
              public primeNGConfig: PrimeNGConfig,
              public titleService: Title,
              public dialogService: DialogService,
              @Inject(FLAT_SETTINGS) public settings: SettingControlModel) {
    // Apply Theme
    this.themeControlFacade.applyTheme();
    
    this.historySharedFacade.requestSetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.loading);
    this.translate.setDefaultLang('de');
    this.translate.use('de');
    
    // PrimeNg Config
    this.translate.get('primeng').pipe(takeUntilDestroyed()).subscribe(data => {
      this.primeNGConfig.setTranslation(data);
    });
    
    i18next.init({
      lng: "de",
      resources: {
        en: { zod: zod_en },
        de: { zod: zod_de },
      },
    });
    z.setErrorMap(zodI18nMap);

    this.router.events.pipe(takeUntilDestroyed()).subscribe((val) => {
      if (val instanceof NavigationEnd) {
        // Used to redirect after login
        if (!val.urlAfterRedirects.startsWith('/login')) {
          this.authContorlService.setRedirectUrl(window.location.href);
        }
        
        this.historySharedFacade.requestSetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none);
      }
      if (val instanceof ActivationEnd && val.snapshot.data.title) {
        this.#title = val.snapshot.data.title;
        if (!this.#title) return;
        this.titleService.setTitle(this.translate.instant('global.title_service_prefix') + this.translate.instant(this.#title));
      }
    });
    
    this.translate.onDefaultLangChange.pipe(take(1)).subscribe(_ => {
      if (this.#title) {
        this.titleService.setTitle(this.translate.instant('global.title_service_prefix') + this.translate.instant(this.#title));
      }
    });
    
    this.appSharedFacade.requestMetadata().subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });

    // open Session Refresh Dialog when user changes to null
    this.authControlFacade.user$.pipe(takeUntilDestroyed(), pairwise(), filter(([prev, curr]) => prev !== null && curr === null )).subscribe(() => {
      const iframe = document.createElement('iframe');
      iframe.src = this.authContorlService.getOidcAuthLoginUrl();
      this.#refreshDialogRef = this.dialogService.open(SessionRefreshDialogComponent, {
        header: this.translate.instant('global.session_refresh'),
        closable: false,
        maximizable: true,
        closeOnEscape: false,
        dismissableMask: false,
        height: 'min(90%, 600px)',
        width: 'min(90%, 1000px)'
      });
    });

    // interval to check session
    from(interval(10 * 1000)).pipe(takeUntilDestroyed(), filter(() => document.hidden === false)).subscribe(() => {
      this.checkSession();
    });
  }

  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent) {
    if (event.data === 'session-refresh-complete') {
      this.checkSession(true);
      setTimeout(() => {
        // Destroy Dialog with 1s delay to better inform user
        if (this.#refreshDialogRef) this.#refreshDialogRef.destroy();
      }, 1000);
    }
  }
  
  @HostListener('window:focus')
  onFocus() {
    // Check Session on window focus
    this.checkSession(true);
  }

  checkSession(force = false) {
    // Check Session every 10 minutes
    if (this.#lastSessionCheck + 1000 * 60 * 10 < Date.now() || force) {
      this.#lastSessionCheck = Date.now();
      this.appSharedFacade.requestMe().subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    }
  }
  
  @HostListener('window:beforeunload', ['$event'])
  onLeave(e: BeforeUnloadEvent) {
    if (this.historySharedFacade.snapshot_blockUiType() !== FlatModels.EntityControlModels.BlockUiType.enum.none) { // Ensure no block ui is active
      e.returnValue = true;
    } else {
      const canSave = this.historySharedFacade.snapshot_canSave();
      if (canSave) {
        e.returnValue = canSave;
      }
    }
  }
}
